import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { Phone, Mail, MapPin, Send, MessageCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const contactCards = [
  {
    icon: Phone,
    label: "Call / WhatsApp",
    value: "+91-9502343899",
    href: "tel:+919502343899",
    hint: "Mon–Sat, 9am – 7pm IST",
    testid: "contact-phone-card",
  },
  {
    icon: Mail,
    label: "Email Us",
    value: "info@nexfoxnourish.in",
    href: "mailto:info@nexfoxnourish.in",
    hint: "We reply within 24 hours",
    testid: "contact-email-card",
  },
  {
    icon: MapPin,
    label: "Visit Us",
    value: "Flat# 32, Om Vihar, Aparajita Apartment, Bhoothnath Road, Kankarbhag, Patna, Bihar",
    href: null,
    hint: "New Foxnourish Private Limited",
    testid: "contact-address-card",
  },
];

const initialForm = { name: "", email: "", phone: "", message: "" };

const ContactSection = () => {
  const { ref, isVisible } = useScrollReveal();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(initialForm);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${BACKEND_URL}/api/contact`, form);
      toast.success(res.data.message);
      setForm(initialForm);
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="py-24 bg-[#050505]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          className="text-center mb-14"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.6s ease, transform 0.6s ease",
          }}
        >
          <span className="text-[#D4AF37] text-xs uppercase tracking-[5px] mb-4 block font-medium">
            Reach Out
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#FAFAFA] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="contact-heading"
          >
            Contact Us
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-xl mx-auto">
            Ready to order or have questions? Our team is just a call, message, or email away.
          </p>
        </div>

        {/* Contact Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
          {contactCards.map((card, i) => {
            const Icon = card.icon;
            return (
              <div
                key={card.label}
                className="bg-[#121212] border border-white/5 hover:border-[#D4AF37]/30 rounded-2xl p-7 text-center transition-all duration-400 hover:shadow-[0_10px_40px_rgba(212,175,55,0.1)] group"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(30px)",
                  transition: `opacity 0.6s ease ${i * 0.15}s, transform 0.6s ease ${i * 0.15}s`,
                }}
                data-testid={card.testid}
              >
                <div className="flex justify-center mb-4">
                  <div className="p-4 rounded-full bg-[#D4AF37]/10 group-hover:bg-[#D4AF37]/20 transition-colors duration-300">
                    <Icon className="text-[#D4AF37]" size={22} />
                  </div>
                </div>
                <p className="text-[#A1A1AA] text-xs uppercase tracking-[3px] mb-2">
                  {card.label}
                </p>
                {card.href ? (
                  <a
                    href={card.href}
                    className="text-[#FAFAFA] font-semibold text-sm hover:text-[#D4AF37] transition-colors break-all leading-relaxed"
                    data-testid={`${card.testid}-link`}
                  >
                    {card.value}
                  </a>
                ) : (
                  <p className="text-[#FAFAFA] font-medium text-sm leading-relaxed">
                    {card.value}
                  </p>
                )}
                <p className="text-[#525252] text-xs mt-2">{card.hint}</p>
              </div>
            );
          })}
        </div>

        {/* Contact Form + WhatsApp */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* WhatsApp CTA */}
          <div
            className="lg:col-span-1"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "opacity 0.6s ease 0.4s, transform 0.6s ease 0.4s",
            }}
          >
            <div className="bg-[#121212] border border-white/5 rounded-2xl p-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="p-4 rounded-full bg-green-500/10">
                  <MessageCircle className="text-green-400" size={28} />
                </div>
              </div>
              <h3
                className="text-[#FAFAFA] text-xl font-semibold mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Chat Instantly
              </h3>
              <p className="text-[#A1A1AA] text-sm mb-5">
                Get quick responses on WhatsApp for pricing, samples, and bulk orders.
              </p>
              <a
                href="https://wa.me/919502343899?text=Hi%2C%20I%27m%20interested%20in%20MakhanaG%20products.%20Please%20share%20details."
                target="_blank"
                rel="noreferrer"
                className="block w-full bg-green-500 hover:bg-green-400 text-white py-3.5 rounded-full font-bold text-sm transition-all duration-300 shadow-[0_4px_20px_rgba(34,197,94,0.3)] hover:shadow-[0_4px_30px_rgba(34,197,94,0.5)] hover:scale-105"
                data-testid="contact-whatsapp-btn"
              >
                WhatsApp Us Now
              </a>
              <p className="text-[#525252] text-xs mt-3">Usually replies within minutes</p>
            </div>
          </div>

          {/* Contact Form */}
          <div
            className="lg:col-span-2 bg-[#121212] border border-white/5 rounded-2xl p-6 md:p-8"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "opacity 0.6s ease 0.5s, transform 0.6s ease 0.5s",
            }}
            data-testid="contact-form-card"
          >
            <h3
              className="text-[#FAFAFA] text-2xl font-semibold mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Send Us a Message
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[#A1A1AA] text-[10px] uppercase tracking-[3px] mb-1.5 block">
                    Full Name *
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Your Name"
                    className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] rounded-lg px-4 py-3 text-[#FAFAFA] text-sm placeholder-[#525252] outline-none transition-colors"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="text-[#A1A1AA] text-[10px] uppercase tracking-[3px] mb-1.5 block">
                    Phone Number *
                  </label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 XXXXX XXXXX"
                    className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] rounded-lg px-4 py-3 text-[#FAFAFA] text-sm placeholder-[#525252] outline-none transition-colors"
                    data-testid="contact-phone"
                  />
                </div>
              </div>

              <div>
                <label className="text-[#A1A1AA] text-[10px] uppercase tracking-[3px] mb-1.5 block">
                  Email Address
                </label>
                <input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  type="email"
                  placeholder="you@example.com"
                  className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] rounded-lg px-4 py-3 text-[#FAFAFA] text-sm placeholder-[#525252] outline-none transition-colors"
                  data-testid="contact-email"
                />
              </div>

              <div>
                <label className="text-[#A1A1AA] text-[10px] uppercase tracking-[3px] mb-1.5 block">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="How can we help you? Ask about pricing, grades, samples, or bulk orders..."
                  className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] rounded-lg px-4 py-3 text-[#FAFAFA] text-sm placeholder-[#525252] outline-none transition-colors resize-none"
                  data-testid="contact-message"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] text-black py-4 rounded-full font-bold text-base hover:bg-[#F4E3B2] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:scale-[1.02] shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                data-testid="contact-submit-btn"
              >
                <Send size={16} />
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
